import Layout from "@/components/layout/Layout";
import { Shield, Heart, Users, Target, Award, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

// Blue vs Blue branded images
const MISSION_IMAGE = "https://customer-assets.emergentagent.com/job_0151f542-6e75-4786-88b8-3312ff27537a/artifacts/gw5nltxv_685162422275311696.jpeg";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_6cd70ab0-164b-4026-90ab-4bbce9b2006d/artifacts/koo5s91n_BluevsBlue1%20%281%29.png";

const values = [
  {
    icon: Shield,
    title: "Trust & Integrity",
    description: "We operate with complete transparency and accountability in all our programs and financial management."
  },
  {
    icon: Heart,
    title: "Compassion",
    description: "We approach every individual with empathy, understanding the personal impact of prostate cancer."
  },
  {
    icon: Users,
    title: "Community",
    description: "We believe in the power of community to create lasting change and support those in need."
  },
  {
    icon: Target,
    title: "Early Detection",
    description: "Our core mission focuses on promoting early detection as the key to saving lives."
  }
];

export default function AboutPage() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="gradient-hero py-20" data-testid="about-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">About Us</span>
            <h1 className="text-heading-1 text-white mt-4 mb-6">Our Story</h1>
            <p className="text-white/80 text-xl leading-relaxed">
              Blue vs Blue was founded with a simple yet powerful mission: to save lives through 
              awareness and early detection of prostate cancer among first responders and their communities.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-spacing" data-testid="mission-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Our Mission</span>
              <h2 className="text-heading-2 text-deep-blue mt-2 mb-6">
                Bringing Awareness to Those Who Serve
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Blue vs Blue Prostate Cancer Awareness Organization is dedicated to bringing prostate
                cancer awareness to first responders across the United States and to the communities
                they serve. We believe that by spreading prostate cancer awareness to these people,
                their lives and the lives of their family and community members will be saved.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-awareness-blue flex-shrink-0 mt-0.5" />
                  <p className="text-slate-600">Partner with law enforcement agencies nationwide</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-awareness-blue flex-shrink-0 mt-0.5" />
                  <p className="text-slate-600">Provide free educational programs and resources</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-awareness-blue flex-shrink-0 mt-0.5" />
                  <p className="text-slate-600">Reach rural and traditionally marginalized communities</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-awareness-blue flex-shrink-0 mt-0.5" />
                  <p className="text-slate-600">Promote early detection and regular screenings</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src={MISSION_IMAGE}
                alt="Community group"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="section-spacing bg-slate-50" data-testid="vision-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Our Vision</span>
            <h2 className="text-heading-2 text-deep-blue mt-2 mb-6">
              A Future Where Early Detection is the Norm
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              Our goal is to spread awareness to first responders wearing the badge of the importance
              of early detection of prostate cancer for themselves and the men in their families.
              Early detection is the key.
            </p>
          </div>
        </div>
      </section>

      {/* Goals Section */}
      <section className="section-spacing" data-testid="goals-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Our Goals</span>
            <h2 className="text-heading-2 text-deep-blue mt-2">What We're Working Toward</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-100">
              <div className="w-12 h-12 bg-awareness-blue/10 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-awareness-blue" />
              </div>
              <h3 className="text-heading-3 text-deep-blue mb-3">First Responder Awareness</h3>
              <p className="text-slate-600">
                To spread awareness to the men and women wearing the badge of the importance of the 
                early detection of prostate cancer for themselves and/or the men in their families.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-100">
              <div className="w-12 h-12 bg-awareness-blue/10 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-awareness-blue" />
              </div>
              <h3 className="text-heading-3 text-deep-blue mb-3">Community Outreach</h3>
              <p className="text-slate-600">
                For first responder agencies to expand this message to rural and traditionally 
                marginalized communities by creating awareness, free educational programs, and 
                community outreach initiatives.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-spacing bg-deep-blue" data-testid="values-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Our Values</span>
            <h2 className="text-heading-2 text-white mt-2">What Drives Us</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-8 h-8 text-awareness-blue" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 font-heading">{value.title}</h3>
                <p className="text-white/70 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 501c3 Section */}
      <section className="section-spacing" data-testid="nonprofit-section">
        <div className="container-custom">
          <div className="bg-slate-50 rounded-2xl p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-deep-blue rounded-full flex items-center justify-center">
                  <Award className="w-12 h-12 text-awareness-blue" />
                </div>
              </div>
              <div>
                <h2 className="text-heading-2 text-deep-blue mb-4">501(c)(3) Nonprofit Organization</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Blue vs Blue is a registered 501(c)(3) nonprofit organization. This means your 
                  donations are tax-deductible to the fullest extent allowed by law. We are committed 
                  to financial transparency and responsible stewardship of all contributions.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  100% of your donation goes toward our mission of spreading prostate cancer awareness 
                  and supporting early detection programs for first responders and communities across 
                  the United States.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-awareness-blue" data-testid="about-cta">
        <div className="container-custom text-center">
          <h2 className="text-heading-2 text-deep-blue mb-4">Join Our Mission</h2>
          <p className="text-deep-blue/80 text-lg mb-8 max-w-2xl mx-auto">
            Whether through donating, volunteering, or spreading the word, you can help us 
            save lives through awareness.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/donate">
              <Button className="bg-deep-blue text-white hover:bg-[#162e4d] font-bold px-8 py-4 rounded-md" data-testid="about-donate-btn">
                <Heart className="w-5 h-5 mr-2" />
                Donate Now
              </Button>
            </Link>
            <Link to="/get-involved">
              <Button className="bg-white text-deep-blue hover:bg-slate-100 font-semibold px-8 py-4 rounded-md" data-testid="about-volunteer-btn">
                Get Involved
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
